import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";

import { ErrorPage } from "@mobsuccess-devops/react-ui/ErrorPage";
import { Loader } from "@mobsuccess-devops/react-ui/Loader";
import { useQueryClient } from "@tanstack/react-query";

import type { AxiosInstances } from "../../features/axios-instances";
import {
  AuthContext,
  type AuthContextType,
  type AuthorizationSession,
  type CognitoUserSession,
  type RemoteAuthSession,
  getOpaqueAuthorization,
  refreshSessionBearer,
  setPatAuthorization,
  useSession,
  useSignout,
} from "../../public/auth/auth";

import {
  AuthRedirectLoopError,
  redirectToAuth,
  redirectToLogout,
  shouldRedirectToAuth,
  useAuthReturnParams,
} from "./auth-redirect";
import { createCatch401ErrorInterceptor } from "./catch-401";

type SessionProviderProps = {
  children: JSX.Element;
  userPoolsWebClientId: string;
  axiosInstances: AxiosInstances;
};

/**
 * Thin auth provider for non-auth webapps. It only *consumes* a session: it
 * never renders a login UI. When no valid session can be resolved it hands off
 * to the centralized auth app: authentication is owned exclusively by that
 * app.
 */
function SessionProvider({
  children,
  axiosInstances,
  userPoolsWebClientId,
}: SessionProviderProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const authorization = getOpaqueAuthorization();

  const {
    isLoading,
    isFetching,
    data: sessionCognito,
    error: sessionError,
  } = useSession({
    queryClient,
    axiosInstances,
    userPoolsWebClientId,
    trustDevice: false,
    validateSession: true,
  });
  const [isRedirectLoop, setIsRedirectLoop] = useState(false);

  const { mutateAsync: signOutAsync } = useSignout({
    postSignout(options) {
      setPatAuthorization(null);
      if (options?.redirect) {
        window.location.assign(options.redirect);
      }
    },
  });

  const jwt = useMemo(() => {
    if (!sessionCognito?.tokens?.accessToken) {
      return null;
    }
    const accessToken = sessionCognito.tokens.accessToken
      .toString()
      .replace(/^jwt:/, "");
    return `Bearer jwt:${accessToken}`;
  }, [sessionCognito]);

  const sessionAuthorization: AuthorizationSession | null = authorization
    ? { authorization }
    : null;
  const session:
    CognitoUserSession | RemoteAuthSession | AuthorizationSession | null =
    sessionAuthorization || sessionCognito || null;

  const redirectToAuthApp = useCallback(async () => {
    try {
      redirectToAuth();
    } catch (error) {
      if (!(error instanceof AuthRedirectLoopError)) {
        throw error;
      }
      setIsRedirectLoop(true);
    }
  }, []);

  axiosInstances.useResponseInterceptor(
    "catch-401",
    (response) => response,
    createCatch401ErrorInterceptor(axiosInstances, queryClient),
  );

  useEffect(() => {
    const unregister = axiosInstances.setBearerRefresher(() =>
      refreshSessionBearer({
        queryClient,
        axiosInstances,
        userPoolsWebClientId,
        trustDevice: false,
        validateSession: true,
      }),
    );
    return unregister;
  }, [axiosInstances, queryClient, userPoolsWebClientId]);

  useEffect(() => {
    const listener = async (event: Event) => {
      const rawDetail: unknown =
        event instanceof CustomEvent ? event.detail : null;
      let redirect: string | undefined;
      if (
        typeof rawDetail === "object" &&
        rawDetail !== null &&
        "redirect" in rawDetail &&
        typeof rawDetail.redirect === "string"
      ) {
        redirect = rawDetail.redirect;
      }
      await signOutAsync(redirect ? { redirect } : undefined);
      if (!redirect) {
        redirectToLogout();
      }
    };
    window.addEventListener("sign-out", listener);
    return () => {
      window.removeEventListener("sign-out", listener);
    };
  }, [signOutAsync]);

  // No valid session and not currently resolving one: hand off to the auth
  // app, unless an auth-app callback is still pending (redirecting then would
  // loop between here and the auth app). This is the only automatic redirect:
  // fetchSession resolves to null and leaves the navigation to this effect, so
  // the breaker counts one redirect per round-trip.
  useEffect(() => {
    if (
      !session &&
      !isLoading &&
      !isFetching &&
      !sessionError &&
      shouldRedirectToAuth(useAuthReturnParams.getState())
    ) {
      void redirectToAuthApp();
    }
  }, [session, isLoading, isFetching, sessionError, redirectToAuthApp]);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      session,
      jwt,
      pat: authorization,
      bearer: jwt || (authorization ? `Bearer ${authorization}` : null),
      isLoggedIn: !!jwt || !!authorization,
      signOut: signOutAsync,
    }),
    [session, jwt, authorization, signOutAsync],
  );

  // A rejected session query (fetchSession already signed out) or a tripped
  // redirect breaker is terminal: an error page rather than an endless loader.
  if (!session && !isLoading && (sessionError || isRedirectLoop)) {
    return <ErrorPage.Any />;
  }

  return (
    <AuthContext value={contextValue}>
      {session && !isLoading ? (
        children
      ) : (
        <Loader.Block>
          <Loader.Ellipsis />
        </Loader.Block>
      )}
    </AuthContext>
  );
}

const MemoizedComponent = memo(SessionProvider);
export { MemoizedComponent as SessionProvider };
